import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useScrapingProgress } from '../hooks/useScrapingProgress';
import { useNotifications } from './NotificationContext';
import { apiService } from '../services/api';
import { API_CONFIG } from '../config/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScrapingContextValue {
  /** Currently active scraping job per property */
  activeJobs: Map<string, ActiveScrapingJob>;
  /** Start scraping for a property */
  startScraping: (propertyId: string, propertyName?: string) => Promise<string | null>;
  /** Track an existing running job discovered via API (e.g. on page refresh) */
  trackExistingJob: (propertyId: string, jobId: string, propertyName?: string) => void;
  /** Get the active jobId for a property */
  getJobId: (propertyId: string) => string | null;
}

interface ActiveScrapingJob {
  jobId: string;
  propertyId: string;
  propertyName?: string;
  startedAt: Date;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ScrapingContext = createContext<ScrapingContextValue | null>(null);

/**
 * Headless component that manages WebSocket progress for a single active job.
 * Sends notification on completion/failure.
 */
function ScrapingJobTracker({
  job,
  onComplete,
}: {
  job: ActiveScrapingJob;
  onComplete: (propertyId: string) => void;
}) {
  const progress = useScrapingProgress(job.jobId);
  const { addNotification } = useNotifications();
  const notifiedRef = useRef(false);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  useEffect(() => {
    if (notifiedRef.current) return;

    const p = progressRef.current;
    if (p.completed) {
      notifiedRef.current = true;
      const label = job.propertyName || 'Property';
      addNotification({
        type: 'success',
        title: 'Knowledge Base populated',
        description: `${label}: ${p.entitiesExtracted} entities extracted from ${p.scrapedPageTitles.length} pages`,
        link: `/properties/${job.propertyId}/knowledge-base`,
      });
      onComplete(job.propertyId);
    } else if (p.failed) {
      notifiedRef.current = true;
      addNotification({
        type: 'error',
        title: 'Knowledge Base scraping failed',
        description: p.errors[0] || 'An error occurred during scraping',
        link: `/properties/${job.propertyId}/knowledge-base`,
      });
      onComplete(job.propertyId);
    }
  }, [progress.completed, progress.failed, job, addNotification, onComplete]);

  return null;
}

export function ScrapingProvider({ children }: { children: ReactNode }) {
  const [activeJobs, setActiveJobs] = useState<Map<string, ActiveScrapingJob>>(new Map());
  const cleanupTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Shared helper to add a job to the active map
  const addJob = useCallback((propertyId: string, jobId: string, propertyName?: string) => {
    setActiveJobs((prev) => {
      const next = new Map(prev);
      next.set(propertyId, { jobId, propertyId, propertyName, startedAt: new Date() });
      return next;
    });
  }, []);

  const handleComplete = useCallback((propertyId: string) => {
    // Keep job for 5s so the KB page can read the final state, then remove
    const timer = setTimeout(() => {
      setActiveJobs((prev) => {
        const next = new Map(prev);
        next.delete(propertyId);
        return next;
      });
      cleanupTimersRef.current.delete(propertyId);
    }, 5000);
    cleanupTimersRef.current.set(propertyId, timer);
  }, []);

  // Clean up timers on unmount
  useEffect(() => {
    const cleanupTimers = cleanupTimersRef.current;
    return () => {
      for (const timer of cleanupTimers.values()) clearTimeout(timer);
    };
  }, []);

  const startScraping = useCallback(async (propertyId: string, propertyName?: string): Promise<string | null> => {
    try {
      const result = await apiService.post<{ success: boolean; jobId?: string; error?: string }>(
        API_CONFIG.endpoints.knowledgeBase.refetch(propertyId),
        {},
      );
      if (result.jobId) {
        addJob(propertyId, result.jobId, propertyName);
        return result.jobId;
      }
      return null;
    } catch (err) {
      // On any failure (409 "already running", network error, etc.),
      // check if there's an active job we can pick up
      try {
        const data = await apiService.get<{ active: boolean; job: { jobId: string } | null }>(
          API_CONFIG.endpoints.knowledgeBase.scrapingStatus(propertyId),
        );
        if (data.active && data.job?.jobId) {
          addJob(propertyId, data.job.jobId, propertyName);
          return data.job.jobId;
        }
      } catch {
        // fall through
      }
      throw err;
    }
  }, [addJob]);

  const trackExistingJob = useCallback((propertyId: string, jobId: string, propertyName?: string) => {
    setActiveJobs((prev) => {
      if (prev.has(propertyId)) return prev; // Already tracking
      const next = new Map(prev);
      next.set(propertyId, { jobId, propertyId, propertyName, startedAt: new Date() });
      return next;
    });
  }, []);

  const getJobId = useCallback(
    (propertyId: string): string | null => {
      return activeJobs.get(propertyId)?.jobId ?? null;
    },
    [activeJobs],
  );

  return (
    <ScrapingContext.Provider value={{ activeJobs, startScraping, trackExistingJob, getJobId }}>
      {/* Render headless trackers for each active job */}
      {Array.from(activeJobs.values()).map((job) => (
        <ScrapingJobTracker key={job.jobId} job={job} onComplete={handleComplete} />
      ))}
      {children}
    </ScrapingContext.Provider>
  );
}

export function useScraping(): ScrapingContextValue {
  const ctx = useContext(ScrapingContext);
  if (!ctx) throw new Error('useScraping must be used within ScrapingProvider');
  return ctx;
}
